import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import type { AuthenticationResult } from '@azure/msal-browser';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { ImpersonationService } from '../../../services/impersonation.service';
import { CryptoService } from '../../../storage/crypto.service';
import { IndexedDBStore } from '../../../storage/indexed-db.store';
import { AUTH_EVENTS, DEFAULT_TOKEN_EXPIRY_MS } from '../../auth-core/constants/auth.constants';
import type { IAuthLifecycleService, LoginOptions } from '../../auth-core/interfaces/auth-core.interfaces';
import { AuthSessionStatus } from '../../auth-core/models/auth-session.model';
import type { AuthTokenEnvelope } from '../../auth-core/models/auth-token-envelope.model';
import { createEmptyUserProfile, createUserProfile } from '../../auth-core/models/user-profile.model';
import { AuthStateService } from '../../auth-core/services/auth-state.service';
import { AUTH_BOOTSTRAP_CONFIG } from '../../auth-core/tokens/auth-config.token';
import { AUTH_LOGGER } from '../../auth-core/tokens/auth-logger.token';
import { MSAL_INSTANCE } from '../../auth-core/tokens/msal.token';
import { LOOKUP_API_BASE_URL } from '../../../http/api-client.tokens';
import { GlobalLoadingService } from '@complexcare/harmony';
import { checkNetworkOnline } from '../../../offline-auth/network-connectivity.util';
import { OfflineAuthService } from '../../../offline-auth/offline-auth.service';
import { decodeJwt } from '../../auth-core/utils/jwt.util';
import { normalizeStringArray } from '../../authorization/utils/claims-parser.util';
import { broadcastAuthEvent, createAuthBroadcastChannel } from '../../session-sync/utils/auth-sync.util';
import {
  clearLogoutIntent,
  clearSession,
  clearToken,
  hasLogoutIntent,
  markLogoutIntent,
  releaseInteractionLock,
  saveToken,
  tryAcquireInteractionLock,
  waitForInteractionLockRelease,
} from '../../session-sync/utils/session-storage.util';

const DEFAULT_SCOPES = ['openid', 'profile', 'offline_access'];
const DEFAULT_REFRESH_LEAD_TIME_SECONDS = 5 * 60;

@Injectable({ providedIn: null })
/** Controls shell authentication lifecycle actions: login, logout, and refresh. */
export class ShellAuthLifecycleService implements IAuthLifecycleService {
  private readonly authState = inject(AuthStateService);
  private readonly msalInstance = inject(MSAL_INSTANCE, { optional: true });
  private readonly authConfig = inject(AUTH_BOOTSTRAP_CONFIG, { optional: true });
  private readonly logger = inject(AUTH_LOGGER);
  private readonly cryptoService = inject(CryptoService);
  private readonly impersonationService = inject(ImpersonationService);
  private readonly lookupApiUrl = inject(LOOKUP_API_BASE_URL, { optional: true });
  private readonly loadingService = inject(GlobalLoadingService, { optional: true });
  private readonly router = inject(Router, { optional: true });
  private readonly offlineAuth = inject(OfflineAuthService, { optional: true });
  private readonly channel = createAuthBroadcastChannel();
  private refreshInFlight$: Observable<void> | null = null;

  private performMsalLogoutRedirect(): void {
    const postLogoutRedirectUri =
      this.authConfig?.postLogoutRedirectUri ??
      (typeof window !== 'undefined' ? `${window.location.origin}/logout` : 'http://localhost:4300/logout');

    if (!this.msalInstance) {
      window.location.href = postLogoutRedirectUri;
      return;
    }

    const msal = this.msalInstance;
    const account = msal.getAllAccounts()[0] ?? null;
    const logoutHint = account?.username || undefined;

    msal.setActiveAccount(null);

    // Acquire interaction lock to prevent concurrent MSAL operations
    const lockAcquired = tryAcquireInteractionLock();

    if (!lockAcquired) {
      // Another tab is performing an interaction; wait for it to complete
      waitForInteractionLockRelease().then(() => {
        this.performMsalLogoutRedirect();
      });
      return;
    }

    this.loadingService?.show('Signing out...');

    // Clear cached accounts/tokens before redirecting to IdP logout endpoint
    msal
      .clearCache()
      .then(() =>
        msal.logoutRedirect({
          account,
          logoutHint,
          postLogoutRedirectUri,
        })
      )
      .catch((err: unknown) => {
        this.loadingService?.hide();
        // Release lock before handling error
        releaseInteractionLock();

        // Check if error is interaction_in_progress
        const error = err as { errorCode?: string; message?: string };
        if (error?.errorCode === 'interaction_in_progress' || error?.message?.includes('interaction_in_progress')) {
          // Another interaction is already in progress; use fallback logout
          this.logger.warn('[conductor/auth] MSAL interaction in progress, using fallback logout');
          this.performFallbackLogout();
        } else {
          this.logger.error('[conductor/auth] logout redirect flow failed:', err);
          clearLogoutIntent();
          this.setSignedOutState();
          // Navigate to post-logout redirect after a brief delay
          setTimeout(() => {
            window.location.href = postLogoutRedirectUri;
          }, 100);
        }
      });
  }

  /**
   * Fallback logout when MSAL is unavailable or interaction is in progress. Clears session state and redirects
   * immediately without MSAL interaction.
   */
  private performFallbackLogout(): void {
    try {
      this.setSignedOutState();
      clearLogoutIntent();
    } catch (err) {
      this.logger.error('[conductor/auth] failed to clear session state:', err);
    } finally {
      // Release lock and redirect
      this.loadingService?.hide();
      releaseInteractionLock();
      const targetUri = this.authConfig?.postLogoutRedirectUri || '/logout';
      setTimeout(() => {
        window.location.href = targetUri;
      }, 0);
    }
  }

  private setSignedOutState(): void {
    this.authState.updateSession({
      status: AuthSessionStatus.SignedOut,
      subjectId: '',
      issuedAt: '',
      expiresAt: '',
      lastActivityAt: '',
      idleWarningAt: null,
      graceEndsAt: null,
      customAccessToken: '',
      idToken: '',
      permissions: [],
      userProfile: createEmptyUserProfile(),
    });
  }

  constructor() {
    // Listen for LOGOUT events from other tabs and force full logout/reauth
    this.channel.onmessage = (event: MessageEvent<{ type: string }>) => {
      if (event.data?.type === AUTH_EVENTS.LOGOUT) {
        // Clear impersonation state first (if any active impersonation)
        void this.impersonationService.clearImpersonationStateAndData();
        // Clear session and tokens
        markLogoutIntent();
        this.setSignedOutState();
        clearSession();
        clearToken();

        void (async () => {
          const isOnline = await checkNetworkOnline(this.lookupApiUrl ?? undefined);
          if (!isOnline) {
            if (this.offlineAuth) {
              await this.offlineAuth.logoutOffline();
            } else {
              try {
                sessionStorage.removeItem('symphony.offline.returnUrl');
                sessionStorage.removeItem('auth_session_active');
              } catch {}
              if (this.router) {
                await this.router.navigate(['/login']);
              } else if (typeof window !== 'undefined') {
                window.location.assign('/login');
              }
            }
            return;
          }

          if (this.offlineAuth) {
            try {
              await this.offlineAuth.clearSession();
            } catch {}
          }

          this.performMsalLogoutRedirect();
        })();
      }
    };
  }

  /** Returns scopes used for login and silent token operations. */
  private get scopes(): string[] {
    return this.authConfig?.scopes ?? DEFAULT_SCOPES;
  }

  private get refreshLeadTimeSeconds(): number {
    return this.authConfig?.tokenRefreshPolicy?.refreshLeadTimeSeconds ?? DEFAULT_REFRESH_LEAD_TIME_SECONDS;
  }

  /**
   * Initiate login via MSAL redirect only if session is not active and user is not logged in. The browser navigates to
   * the identity provider; the app resumes at the redirectUri after authentication with the result handled by
   * ShellAuthBootstrapService.handleRedirectPromise().
   */
  login(options?: LoginOptions): void {
    // Authentication is disabled by configuration — do not redirect to the IdP.
    if (this.authConfig?.enableAuthentication === false) {
      return;
    }

    void (async () => {
      // For online B2C login, verify standard network connectivity (navigator.onLine)
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        this.logger.warn('[conductor/auth] Cannot initiate B2C login while browser is offline');
        return;
      }

      const session = this.authState.getSession();
      const forceReauthenticate = options?.force ?? hasLogoutIntent() ?? false;
      const isActive = session.status === AuthSessionStatus.SignedIn || session.status === AuthSessionStatus.SigningIn;
      const isLoggedIn = !!this.msalInstance && this.msalInstance.getAllAccounts().length > 0;

      if (!forceReauthenticate && (isActive || isLoggedIn)) {
        return;
      }

      if (!this.msalInstance) {
        if (this.authState.getSession().status !== AuthSessionStatus.SignedOut) {
          this.setSignedOutState();
        }
        return;
      }

      // Release any stale interaction lock before login
      releaseInteractionLock();
      const lockAcquired = tryAcquireInteractionLock();
      if (!lockAcquired) {
        waitForInteractionLockRelease().then(() => {
          this.login(options);
        });
        return;
      }

      this.impersonationService.clearImpersonationState();

      // Explicitly clear any lingering logout intent now that login has been initiated
      clearLogoutIntent();

      this.authState.updateSession({ status: AuthSessionStatus.SigningIn });

      const promptValue = options?.prompt ?? (forceReauthenticate ? 'login' : undefined);
      const loginRequest = {
        scopes: this.scopes,
        redirectUri: this.authConfig?.redirectUri,
        redirectStartPage: this.authConfig?.redirectUri ?? '/',
        extraQueryParameters: this.authConfig?.extraQueryParameters,
        ...(promptValue ? { prompt: promptValue } : {}),
      };

      this.msalInstance.loginRedirect(loginRequest).catch(async (err: unknown) => {
        releaseInteractionLock();
        this.logger.error('[conductor/auth] loginRedirect failed:', err);

        // A failed interactive redirect must be terminal for this tab. Mark the
        // logout intent before publishing SignedOut so the orchestrator does not
        // immediately start another login redirect loop.
        markLogoutIntent();
        this.msalInstance?.setActiveAccount(null);
        this.msalInstance?.clearCache().catch((cacheError: unknown) => {
          this.logger.warn('[conductor/auth] Failed to clear MSAL cache after login redirect failure:', cacheError);
        });
        this.setSignedOutState();
        clearSession();
        clearToken();
        this.cryptoService.clearKeys().catch((cryptoError) => {
          this.logger.warn('[conductor/auth] Failed to clear crypto keys after login redirect failure:', cryptoError);
        });

        // Determine if error was caused by offline connectivity
        const networkOnline = typeof navigator !== 'undefined' ? navigator.onLine : false;
        const path = typeof window !== 'undefined' ? window.location.pathname : '';

        if (!networkOnline) {
          if (this.router) {
            void this.router.navigate(['/login']);
          } else if (typeof window !== 'undefined' && !path.startsWith('/login') && !path.startsWith('/offline')) {
            window.location.assign('/login');
          }
        } else {
          // If online and MSAL redirect failed, route to /error — NEVER /unauthorized!
          if (this.router) {
            void this.router.navigate(['/error']);
          } else if (typeof window !== 'undefined' && !path.startsWith('/error') && !path.startsWith('/login')) {
            window.location.assign('/error');
          }
        }
      });
    })();
  }

  /**
   * Initiate logout. When online, performs MSAL redirect to identity provider. When offline, executes complete offline
   * logout (clears sessions from storage/IndexedDB and navigates to the offline PIN /login route).
   *
   * Also clears cryptographic keys to ensure PHI encryption keys don't persist after logout.
   */
  logout(): void {
    this.authState.updateSession({ status: AuthSessionStatus.SigningOut });
    this.loadingService?.show('Signing out...');

    // Clear impersonation state first (if any active impersonation)
    // This ensures impersonation doesn't persist across logout/login cycles
    void this.impersonationService.clearImpersonationStateAndData();

    markLogoutIntent();
    // Note: deliberately NOT clearing the cached offline user profile (name/email/role) here —
    // it isn't PHI, and is needed to display the correct name after a subsequent offline PIN login.
    clearSession();
    clearToken();

    // Close open IndexedDB connections cleanly
    IndexedDBStore.closeAll();

    // Clear in-memory crypto keys asynchronously - fire and forget since we're redirecting
    // Stored keys in KeyStoreService are retained so NPs can resume offline assessments
    this.cryptoService.clearKeys().catch((err) => {
      this.logger.warn('[conductor/auth] Failed to clear crypto keys during logout:', err);
    });

    broadcastAuthEvent(this.channel, { type: AUTH_EVENTS.LOGOUT });

    void (async () => {
      // Check network connectivity (via resilient health probe)
      const isOnline = await checkNetworkOnline(this.lookupApiUrl ?? undefined);

      if (!isOnline) {
        this.loadingService?.hide();
        if (this.offlineAuth) {
          await this.offlineAuth.logoutOffline();
        } else {
          this.setSignedOutState();
          try {
            sessionStorage.removeItem('symphony.offline.returnUrl');
            sessionStorage.removeItem('auth_session_active');
          } catch {}
          if (this.router) {
            await this.router.navigate(['/login']);
          } else if (typeof window !== 'undefined') {
            window.location.assign('/login');
          }
        }
        return;
      }

      // Online: clear any offline session remnants as well
      if (this.offlineAuth) {
        try {
          await this.offlineAuth.clearSession();
        } catch (err) {
          this.logger.warn('[conductor/auth] Error clearing offline session during online logout:', err);
        }
      }

      // Online logout: release interaction locks before redirecting
      releaseInteractionLock();

      if (this.msalInstance) {
        const account = this.msalInstance.getActiveAccount() ?? this.msalInstance.getAllAccounts()[0] ?? null;
        const postLogoutRedirectUri =
          this.authConfig?.postLogoutRedirectUri ??
          (typeof window !== 'undefined' ? `${window.location.origin}/logout` : 'http://localhost:4300/logout');

        try {
          await this.msalInstance.clearCache();
        } catch (cacheErr) {
          this.logger.warn('[conductor/auth] Error clearing MSAL cache during logout:', cacheErr);
        }

        try {
          await this.msalInstance.logoutRedirect({
            account,
            postLogoutRedirectUri,
          });
          return;
        } catch (err) {
          this.logger.error('[conductor/auth] MSAL logoutRedirect failed, falling back to local /logout:', err);
        }
      }

      // Fallback if msalInstance is not available or redirect failed
      this.setSignedOutState();
      this.loadingService?.hide();
      const fallbackLogoutUri =
        this.authConfig?.postLogoutRedirectUri ??
        (typeof window !== 'undefined' ? `${window.location.origin}/logout` : '/logout');

      if (typeof window !== 'undefined') {
        window.location.replace(fallbackLogoutUri);
      } else if (this.router) {
        void this.router.navigate(['/logout']);
      }
    })();
  }

  /**
   * Attempt a silent token refresh via MSAL acquireTokenSilent. Broadcasts token-refreshed on success, session-expired
   * on failure.
   */
  refreshToken(): Observable<void> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refresh$ = this.executeRefreshToken().pipe(
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.refreshInFlight$ = refresh$;
    return refresh$;
  }

  /** Returns true when the active token expires within the configured lead window. */
  isTokenExpiringSoon(withinSeconds: number = this.refreshLeadTimeSeconds): boolean {
    const session = this.authState.getSession();
    if (session.status !== AuthSessionStatus.SignedIn || !session.expiresAt) {
      return false;
    }

    const expiryMs = Date.parse(session.expiresAt);
    if (Number.isNaN(expiryMs)) {
      return false;
    }

    return expiryMs - Date.now() <= withinSeconds * 1_000;
  }

  /**
   * Refreshes token only if it is expiring within the lead window. Returns true when refresh is performed, false when
   * skipped.
   */
  refreshTokenIfExpiringSoon(withinSeconds: number = this.refreshLeadTimeSeconds): Observable<boolean> {
    if (!this.isTokenExpiringSoon(withinSeconds)) {
      return of(false);
    }

    return this.refreshToken().pipe(map(() => true));
  }

  private executeRefreshToken(): Observable<void> {
    if (!this.msalInstance) {
      return throwError(() => new Error('[conductor/auth] MSAL instance is not available.'));
    }

    const accounts = this.msalInstance.getAllAccounts();
    const account = accounts[0];
    if (!account) {
      return throwError(() => new Error('[conductor/auth] No authenticated account for token refresh.'));
    }

    const silentRequest = {
      scopes: this.scopes,
      account,
      ...(this.authConfig?.extraQueryParameters ? { extraQueryParameters: this.authConfig.extraQueryParameters } : {}),
    };

    return from(this.msalInstance.acquireTokenSilent(silentRequest)).pipe(
      tap((result: AuthenticationResult) => {
        const expiresAt =
          result.expiresOn?.toISOString() ?? new Date(Date.now() + DEFAULT_TOKEN_EXPIRY_MS).toISOString();

        const envelope: AuthTokenEnvelope = {
          accessToken: result.accessToken,
          idToken: result.idToken ?? '',
          scopes: result.scopes,
          expiresAt,
          tenant: result.tenantId ?? '',
        };

        // Extract customAccessToken, permissions, and user profile from idToken payload
        let permissions: string[] = [];
        let userProfile = createEmptyUserProfile();
        const idTokenPayload = decodeJwt(envelope.idToken);
        const customAccessToken = (idTokenPayload?.['customAccessToken'] as string | undefined) ?? '';
        if (customAccessToken) {
          const customTokenPayload = decodeJwt(customAccessToken);
          if (customTokenPayload) {
            permissions = normalizeStringArray(customTokenPayload['entitlement']);
            userProfile = createUserProfile(customTokenPayload);
          }
        }

        saveToken(envelope);
        this.authState.updateSession({
          status: AuthSessionStatus.SignedIn,
          expiresAt,
          lastActivityAt: new Date().toISOString(),
          customAccessToken,
          idToken: envelope.idToken,
          permissions,
          userProfile,
        });
        broadcastAuthEvent(this.channel, { type: AUTH_EVENTS.TOKEN_REFRESHED });
      }),
      map(() => void 0),
      catchError((err: unknown) => {
        broadcastAuthEvent(this.channel, { type: AUTH_EVENTS.SESSION_EXPIRED });
        return throwError(() => err);
      })
    );
  }

  /** Releases cross-tab synchronization resources. */
  ngOnDestroy(): void {
    this.channel.close();
  }
}
